import type { AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type PendingOperatorAction } from "../api/client.js";
import { can } from "../capabilities.js";
import { ConfirmAction } from "../components/ConfirmAction.js";
import { asAmount, asMoment } from "../format.js";

/** What the request asks for, in the words an operator uses rather than the action type's own. */
const ACTIONS: Record<string, string> = {
  "admin.role_change": "Operator role change",
  "blackjack.round_void": "Blackjack round void",
  "roulette.limit_profile_change": "Roulette limit profile",
  "wallet.bonus_grant": "Bonus grant",
  "wallet.play_money_top_up": "Play-money cash top-up",
  "wallet.reversal": "Reversal",
};

const RETIRED_READ_ONLY_ACTIONS = new Set(["roulette.limit_profile_change"]);

const VOID_REASONS: Record<string, string> = {
  authoritative_state_unrecoverable: "authoritative state cannot be recovered",
  certified_artifact_unavailable: "certified artifact is unavailable",
  integrity_check_failed: "integrity check failed",
  rng_evidence_unavailable: "RNG evidence is unavailable",
};

/**
 * How long is left to decide. A request expires after a day, and somebody reading the queue needs
 * to see which rows are about to stop being approvable.
 */
function expiresIn(iso: string): { expired: boolean; tone: string; wording: string } {
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  if (minutes <= 0) return { expired: true, tone: "banned", wording: "expired" };
  if (minutes < 60) return { expired: false, tone: "suspended", wording: `${minutes} min left` };
  return { expired: false, tone: "active", wording: `${Math.round(minutes / 60)} h left` };
}

/** What the operator is about to decide. */
interface Decision {
  action: PendingOperatorAction;
  verdict: "approve" | "reject";
}

const label = (action: PendingOperatorAction): string =>
  ACTIONS[action.actionType] ?? action.actionType;

const isRetiredReadOnlyAction = (action: PendingOperatorAction): boolean =>
  RETIRED_READ_ONLY_ACTIONS.has(action.actionType);

/**
 * What is being asked for, read out of the payload.
 *
 * The payload is `unknown` by contract — each action type carries its own shape, and the server
 * parses it when the action is carried out. Here it is only rendered, so an unreadable payload
 * degrades to its JSON rather than blanking the cell: an approver who cannot tell what they are
 * approving should see *something*, and then refuse it.
 */
function describe(action: PendingOperatorAction): string {
  const payload = action.payload as Record<string, unknown> | null;

  if (
    action.actionType === "admin.role_change" &&
    typeof payload?.email === "string" &&
    typeof payload?.fromRole === "string" &&
    typeof payload?.role === "string"
  )
    return `${payload.email} · ${payload.fromRole.replace("_", " ")} → ${payload.role.replace("_", " ")}`;

  if (action.actionType === "blackjack.round_void" && typeof payload?.reasonCode === "string") {
    return `round ${action.targetId} · ${VOID_REASONS[payload.reasonCode] ?? payload.reasonCode}`;
  }

  if (
    action.actionType === "wallet.reversal" &&
    typeof payload?.originalTransactionId === "string"
  ) {
    return `transaction ${payload.originalTransactionId}`;
  }

  if (
    (action.actionType === "wallet.bonus_grant" ||
      action.actionType === "wallet.play_money_top_up") &&
    typeof payload?.amount === "string" &&
    typeof payload?.currency === "string"
  ) {
    // Shared with the ledger rather than reimplemented: the copy that used to live here dropped the
    // sign handling and rendered a negative as `-2.-50`.
    return asAmount({ amount: payload.amount, currency: payload.currency });
  }

  if (
    action.actionType === "roulette.limit_profile_change" &&
    typeof payload?.profile === "string"
  ) {
    if (
      payload.profile === "custom" &&
      typeof payload.aggregateInsideMaxMinor === "string" &&
      typeof payload.aggregateOutsideMaxMinor === "string" &&
      Array.isArray(payload.chipDenominationsMinor)
    ) {
      return `table ${action.targetId} · custom limits · inside ${asAmount({ amount: payload.aggregateInsideMaxMinor, currency: "XTS" })} · outside ${asAmount({ amount: payload.aggregateOutsideMaxMinor, currency: "XTS" })} · chips ${payload.chipDenominationsMinor.map(String).join(", ")}`;
    }
    return `table ${action.targetId} · ${payload.profile} limits`;
  }

  return JSON.stringify(action.payload);
}

/**
 * The approval queue (D-018).
 *
 * Money actions take two people: one raises the request, another decides it. This screen is the
 * second half. Approving carries the movement out in the same call — there is no separate "now
 * apply it" step, because a decision waiting to be applied is a decision that can be forgotten.
 *
 * An operator's own requests are shown but not decidable. The server refuses them regardless of
 * what this screen renders; hiding the buttons is a courtesy, not the control.
 */
export function Actions({ admin }: { admin: AuthenticatedAdmin }) {
  const [actions, setActions] = useState<PendingOperatorAction[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decided, setDecided] = useState<string | null>(null);
  const [intent, setIntent] = useState<Decision | null>(null);
  const [pending, setPending] = useState(false);
  const mayDecide = (action: PendingOperatorAction) => {
    if (action.actionType === "admin.role_change") return can(admin.role, "admins.approve");
    if (action.actionType === "wallet.reversal") return can(admin.role, "wallet.reversal.approve");
    return can(admin.role, "wallet.adjust.approve");
  };

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const { actions: found } = await api.pendingActions(signal);
      // Soonest to expire first. `expiresIn` exists because urgency matters here, and a queue that
      // computes urgency and then ignores it leaves the row with eight minutes left below the one
      // with twenty hours.
      setActions(
        [...found].sort(
          (a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime(),
        ),
      );
      setError(null);
    } catch (caught) {
      if (signal?.aborted) return;
      setError(caught instanceof ApiError ? caught.message : "Could not load the queue.");
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /** Carry out the decision the dialog was opened for. */
  async function decide(justification: string) {
    if (!intent || isRetiredReadOnlyAction(intent.action)) return;
    setPending(true);
    setError(null);

    try {
      if (intent.verdict === "approve") {
        await api.approveAction(intent.action.id, justification);
      } else await api.rejectAction(intent.action.id, justification);

      // Said out loud rather than left to the row disappearing. Two similar requests in a queue
      // look alike, and "it vanished" is not confirmation of which one was decided.
      setDecided(
        `${intent.verdict === "approve" ? "Approved" : "Rejected"} ${label(intent.action)} — ${describe(intent.action)}, raised by ${intent.action.initiatedByEmail}.`,
      );
      setIntent(null);
      // Reloaded rather than dropped locally: a decision changes what the queue holds, and the
      // server is what knows the rest of it.
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "That decision did not go through.");
    } finally {
      setPending(false);
    }
  }

  if (!actions && !error) {
    return (
      <p className="empty" role="status">
        Loading…
      </p>
    );
  }

  return (
    <section className="body">
      <div className="card">
        <div className="card-bar">
          <span className="label-14-strong">Approval queue</span>
          <span className="label-13 dim">
            {actions?.length === 1 ? "1 request" : `${actions?.length ?? 0} requests`}
          </span>
        </div>

        {/* Outside the dialog, because a decision that failed has no dialog left to show it in. */}
        {error && !intent ? (
          <p className="banner" role="alert">
            {error}
          </p>
        ) : null}

        {decided ? (
          <p className="banner banner-quiet" role="status">
            {decided}
          </p>
        ) : null}

        {actions?.length === 0 ? (
          <p className="empty">Nothing is waiting for a decision.</p>
        ) : (
          <div className="table-scroll">
            <table className="mobile-cards">
              <caption className="visually-hidden">Money actions waiting for a decision</caption>
              <thead>
                <tr>
                  <th scope="col" style={{ width: "18%" }}>
                    Action
                  </th>
                  <th scope="col" style={{ width: "20%" }}>
                    Raised by
                  </th>
                  <th scope="col">Justification</th>
                  <th scope="col" style={{ width: "12%" }}>
                    Expires
                  </th>
                  <th scope="col" style={{ width: "18%" }}>
                    <span className="visually-hidden">Decision</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {actions?.map((action) => {
                  const own = action.initiatedBy === admin.id;
                  const targetSelf =
                    action.actionType === "admin.role_change" && action.targetId === admin.id;
                  const retiredReadOnly = isRetiredReadOnlyAction(action);
                  const { expired, tone, wording } = expiresIn(action.expiresAt);

                  return (
                    <tr key={action.id}>
                      <td className="label-13" data-label="Action">
                        {label(action)}
                        {/*
                          Not truncated: this is the sum somebody is about to authorise, and a
                          figure only readable by hovering is a figure a keyboard user cannot read.
                        */}
                        <div className="label-13 dim">{describe(action)}</div>
                      </td>
                      <td
                        className="label-13 dim truncate"
                        data-label="Raised by"
                        title={action.initiatedByEmail}
                      >
                        {action.initiatedByEmail}
                        <div className="label-13 dim">{asMoment(action.initiatedAt)}</div>
                      </td>
                      <td className="label-13" data-label="Justification">
                        {action.justification}
                      </td>
                      <td data-label="Expires">
                        <span className={`badge badge-${tone}`}>
                          <span className="dot" />
                          {wording}
                        </span>
                      </td>
                      <td data-label="Decision" style={{ textAlign: "right" }}>
                        {/*
                          Own requests are visible but not decidable: seeing that yours is queued is
                          useful, approving it yourself is the failure maker-checker exists to make
                          impossible. The API refuses it either way.
                        */}
                        {retiredReadOnly ? (
                          <span className="label-13 dim">retired — read-only</span>
                        ) : own || targetSelf ? (
                          <span className="label-13 dim">yours to wait on</span>
                        ) : expired ? (
                          // The server refuses an expired request; offering the button anyway would
                          // turn a known outcome into a confusing error.
                          <span className="label-13 dim">too late to decide</span>
                        ) : mayDecide(action) ? (
                          <span className="row-decide">
                            <button
                              className="btn btn-quiet btn-sm"
                              onClick={() => setIntent({ action, verdict: "reject" })}
                              type="button"
                            >
                              Reject
                            </button>
                            <button
                              // Outlined like the other irreversible controls, not filled: the most
                              // consequential button on the screen should not be the one that
                              // attracts the click.
                              className="btn btn-danger btn-sm"
                              onClick={() => setIntent({ action, verdict: "approve" })}
                              type="button"
                            >
                              Approve
                            </button>
                          </span>
                        ) : (
                          <span className="label-13 dim">not yours to decide</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {intent ? (
        <ConfirmAction
          confirmLabel={intent.verdict === "approve" ? "Approve" : "Reject"}
          danger={intent.verdict === "approve"}
          error={error}
          onCancel={() => {
            setIntent(null);
            setError(null);
          }}
          onConfirm={decide}
          pending={pending}
          minimumReason={20}
          prompt={
            intent.verdict === "approve"
              ? `${label(intent.action)} — ${describe(intent.action)}. The change applies as soon as you approve.`
              : `${label(intent.action)} — ${describe(intent.action)}. The request is closed and nothing moves.`
          }
          title={intent.verdict === "approve" ? "Approve this request" : "Reject this request"}
        />
      ) : null}
    </section>
  );
}
